import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fetchWithAuth } from '../../utils/api';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';

interface Signal {
  id: string;
  source_id: string;
  customer_name: string;
  verbatim: string;
  channel: string;
  severity_score: number;
  severity_label: string;
  status: string;
  created_at: string;
  taxonomy_confidence?: number;
}

interface TaxonomySignalsListProps {
  labelId: string;
  labelName: string;
  onSignalClick?: (signalId: string) => void;
}

const TaxonomySignalsList: React.FC<TaxonomySignalsListProps> = ({
  labelId,
  labelName,
  onSignalClick,
}) => {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSignals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Extract the actual label ID from the node ID (format: "label-{labelId}")
      const actualLabelId = labelId.replace('label-', '');

      console.log('Loading signals for label:', labelId, 'actualLabelId:', actualLabelId);

      // Fetch signals with this taxonomy label
      const response = await fetchWithAuth(`/v1/signals/?taxonomy_label=${actualLabelId}`);

      console.log('Signals API response:', response);

      if (response.items) {
        setSignals(response.items);
      } else {
        setSignals([]);
      }
    } catch (err) {
      console.error('Failed to load signals for label:', err);
      setError('Failed to load signals');
      setSignals([]);
    } finally {
      setLoading(false);
    }
  }, [labelId]);

  const getSeverityVariant = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'attention';
      case 'medium':
        return 'outline';
      case 'low':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'secondary';
      case 'open':
        return 'default';
      default:
        return 'outline';
    }
  };

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col p-4">
        <div className="space-y-1">
          <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
            {labelName}
          </h3>
          <p className="text-[13px] leading-[1.7] text-muted-foreground">Loading matching signals.</p>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 p-4">
        <div className="space-y-1">
          <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
            {labelName}
          </h3>
          <p className="text-[13px] leading-[1.7] text-muted-foreground">Unable to load matching signals.</p>
        </div>
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-[13px] leading-[1.7] text-destructive">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-2 p-4">
        <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">{labelName}</h3>
        <p className="text-[13px] leading-[1.7] text-muted-foreground">
          {signals.length} signal{signals.length !== 1 ? 's' : ''} detected
        </p>
      </div>

      <Separator />

      <div className="flex-1 space-y-3 p-4">
        {signals.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-10 text-center text-[13px] leading-[1.7] text-muted-foreground">
            No signals found for this taxonomy label
          </div>
        ) : (
          signals.map((signal) => (
            <button
              key={signal.id}
              type="button"
              className="group w-full rounded-xl border border-border bg-background p-4 text-left transition-[border-color,box-shadow,background-color,transform] hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[var(--shadow-card)]"
              onClick={() => onSignalClick?.(signal.id)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-[13px] font-semibold text-foreground">{signal.source_id}</span>
                    <Badge variant={getSeverityVariant(signal.severity_label)}>{signal.severity_label}</Badge>
                    <Badge variant={getStatusVariant(signal.status)}>{signal.status}</Badge>
                  </div>
                  <div className="text-[13px] leading-[1.7] text-muted-foreground">
                    {signal.customer_name} • {signal.channel}
                  </div>
                </div>
                {signal.taxonomy_confidence && (
                  <div className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                    {Math.round(signal.taxonomy_confidence * 100)}% match
                  </div>
                )}
              </div>
              
              <div className="mt-3 line-clamp-2 text-[13px] leading-[1.7] text-foreground/80">
                {signal.verbatim}
              </div>
              
              <div className="mt-3 text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                {format(new Date(signal.created_at), 'MMM d, yyyy h:mm a')}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default TaxonomySignalsList;
